import { getContrast, getLuminance, mix, rgba, shade } from 'polished';
import styled from 'styled-components';
import { palette } from 'styled-tools';

import { FontSizes, FontWeights, PaletteColor, Text } from '@calm-web/design-system';

export const BACKGROUND_TINT_ALPHA = 0.1;

/** WCAG AA minimum for text below 18pt regular or 14pt bold, which this callout's text is. */
export const WCAG_AA_MIN_CONTRAST = 4.5;

// A palette color dark enough to be text on a light page is shaded toward black until it reads
// at AA against its own tint on every page color the callout ships on. The raw default color
// only reaches 4.3:1 on a gray0 page. Shading starts at 15%, which already clears AA for the
// default color with margin, and steps up for lighter colors such as blue2.
const MIN_TEXT_SHADE = 0.15;
const TEXT_SHADE_STEP = 0.05;
const MAX_TEXT_SHADE = 0.6;

// A light palette color (the white variant on the brand-gradient hero) is text on a dark backdrop.
// Shading it would move it toward that backdrop and cost contrast, so it is left as it is.
const LIGHT_TEXT_LUMINANCE = 0.5;

export const calloutBackground = (color: string): string => rgba(color, BACKGROUND_TINT_ALPHA);

/** The tint as the eye sees it: the translucent background flattened onto the page behind it. */
export const calloutBackgroundOver = (color: string, page: string): string =>
	mix(BACKGROUND_TINT_ALPHA, color, page);

export const calloutTextColor = (color: string, pages: readonly string[]): string => {
	if (getLuminance(color) >= LIGHT_TEXT_LUMINANCE) {
		return color;
	}
	const tints = pages.map(page => calloutBackgroundOver(color, page));
	let textShade = MIN_TEXT_SHADE;
	let text = shade(textShade, color);
	while (textShade < MAX_TEXT_SHADE && tints.some(tint => getContrast(text, tint) < WCAG_AA_MIN_CONTRAST)) {
		textShade += TEXT_SHADE_STEP;
		text = shade(textShade, color);
	}
	return text;
};

export const Wrapper = styled(Text).attrs(() => ({
	el: 'p',
	noMargin: true,
	size: FontSizes.sm,
	textAlign: 'center',
	weight: FontWeights.Demi,
}))<{
	$color: PaletteColor;
}>`
	background: ${p => calloutBackground(palette(p.$color)(p))};
	border-radius: 10px;
	/* The page colors the callout ships on: white cards and the gray0 page behind the checkout. */
	color: ${p => calloutTextColor(palette(p.$color)(p), [palette('white')(p), palette('gray0')(p)])};
	display: inline-block;
	margin: 15px auto;
	padding: 5px 12px;
	text-align: center;
`;
